import {
  CheckCircleOutline,
  ErrorOutline,
  InfoOutlined,
  Replay,
  Send,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import CustomerIntakePublicShell from '../components/CustomerIntake/CustomerIntakePublicShell';
import CustomerIntakeServiceField from '../components/CustomerIntake/CustomerIntakeServiceField';
import {
  createInitialCustomerIntakeValues,
  getCurrentCustomerIntakePublicForm,
  getCustomerIntakeErrorMessage,
  getCustomerIntakePublicForm,
  submitCustomerIntakeResponse,
} from '../utils/customerIntakeApi';
import {
  CustomerIntakeField,
  CustomerIntakeFieldOption,
  CustomerIntakeFormConfig,
  CustomerIntakeFormErrors,
  CustomerIntakeFormValue,
  CustomerIntakeFormValues,
  getCustomerIntakeFieldDescription,
} from '../types/CustomerIntake.type';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

const requiredLabel = (label: string, required?: boolean): ReactNode => (
  <>
    {label}
    {required && (
      <Box component="span" className="required-asterisk" aria-hidden="true">
        {' *'}
      </Box>
    )}
  </>
);

const isEmptyValue = (value: CustomerIntakeFormValue | undefined) => {
  if (typeof value === 'boolean') return !value;
  if (typeof value === 'number') return !Number.isFinite(value);
  if (Array.isArray(value)) return value.length === 0;
  return !value?.trim();
};

const validateCustomerIntakeForm = (
  values: CustomerIntakeFormValues,
  formConfig: CustomerIntakeFormConfig,
): CustomerIntakeFormErrors => {
  const errors: CustomerIntakeFormErrors = {};

  formConfig.sections.forEach((section) => {
    section.fields.forEach((field) => {
      const isVisible =
        !field.conditional ||
        values[field.conditional.fieldId] === field.conditional.value;

      if (!isVisible) return;

      const value = values[field.id];

      if (field.required && isEmptyValue(value)) {
        errors[field.id] = 'This field is required.';
        return;
      }

      if (field.type === 'email' && typeof value === 'string' && value) {
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        if (!isValidEmail) errors[field.id] = 'Enter a valid email address.';
      }

      if (field.type === 'phone' && typeof value === 'string' && value) {
        const normalizedPhone = value.replace(/[\s()-]/g, '');
        const isValidPhone = /^\+?[0-9]{8,15}$/.test(normalizedPhone);
        if (!isValidPhone) errors[field.id] = 'Enter a valid phone number.';
      }

      if (field.type === 'number' && typeof value === 'string' && value) {
        if (!Number.isFinite(Number(value))) {
          errors[field.id] = 'Enter a valid number.';
        }
      }
    });
  });

  return errors;
};

const CustomerIntakeSuccess = ({ onReset }: { onReset: () => void }) => (
  <Container maxWidth="sm" className="customer-intake-page-container">
    <Card className="customer-intake-card customer-intake-success-card">
      <Stack spacing={2} alignItems="center">
        <CheckCircleOutline
          className="customer-intake-success-icon"
          aria-hidden="true"
        />
        <Typography variant="h4" color="secondary.main" fontWeight={700}>
          Thank you for providing your information
        </Typography>
        <Typography color="text.secondary">
          Your information has been recorded. Aquatics HUB will contact you as
          soon as possible.
        </Typography>
        <Button variant="outlined" onClick={onReset} startIcon={<Replay />}>
          Submit another response
        </Button>
      </Stack>
    </Card>
  </Container>
);

const CustomerIntakeError = ({
  onRetry,
  message = 'We could not submit your information. Please check your connection and try again.',
}: {
  onRetry: () => void;
  message?: string;
}) => (
  <Alert
    severity="error"
    icon={<ErrorOutline />}
    action={
      <Button color="inherit" size="small" onClick={onRetry}>
        Try again
      </Button>
    }
  >
    {message}
  </Alert>
);

const CustomerIntakeForm = () => {
  const publicToken = new URLSearchParams(window.location.search).get('token');
  const [formConfig, setFormConfig] = useState<CustomerIntakeFormConfig | null>(
    null,
  );
  const [activePublicToken, setActivePublicToken] = useState(publicToken || '');
  const [policyVersion, setPolicyVersion] = useState('');
  const [values, setValues] = useState<CustomerIntakeFormValues>({});
  const [errors, setErrors] = useState<CustomerIntakeFormErrors>({});
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const idempotencyKeyRef = useRef('');

  const loadForm = async () => {
    setLoading(true);
    try {
      const form = publicToken
        ? await getCustomerIntakePublicForm(publicToken)
        : await getCurrentCustomerIntakePublicForm();
      setFormConfig(form.definition);
      setValues(createInitialCustomerIntakeValues(form.definition));
      setActivePublicToken(form.publicToken || publicToken || '');
      setPolicyVersion(form.privacyPolicyVersion);
      setLoadError('');
    } catch (error) {
      setLoadError(
        getCustomerIntakeErrorMessage(
          error,
          'Unable to load the customer information form.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadForm();
  }, [publicToken]);

  const visibleField = (field: CustomerIntakeField) =>
    !field.conditional ||
    values[field.conditional.fieldId] === field.conditional.value;

  const clearFieldError = (fieldId: string) => {
    if (!errors[fieldId]) return;

    setErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[fieldId];
      return nextErrors;
    });
  };

  const handleValueChange = (
    fieldId: string,
    value: CustomerIntakeFormValue,
  ) => {
    setValues((currentValues) => {
      const nextValues = { ...currentValues, [fieldId]: value };

      if (fieldId === 'customerType' && value !== 'CHILD') {
        nextValues.guardianName = '';
      }

      return nextValues;
    });
    clearFieldError(fieldId);
    setSubmitError('');
  };

  const handleCheckboxChange = (fieldId: string, optionValue: string) => {
    const currentValue = values[fieldId];
    const currentOptions = Array.isArray(currentValue) ? currentValue : [];
    const nextOptions = currentOptions.includes(optionValue)
      ? currentOptions.filter((value) => value !== optionValue)
      : [...currentOptions, optionValue];

    handleValueChange(fieldId, nextOptions);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formConfig || !activePublicToken) return;

    const nextErrors = validateCustomerIntakeForm(values, formConfig);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setSubmitState('idle');
      return;
    }

    setSubmitState('submitting');
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = `customer-intake-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 12)}`;
    }

    try {
      await submitCustomerIntakeResponse(
        activePublicToken,
        {
          answers: values,
          privacyPolicyVersion: policyVersion,
          source: 'QR',
        },
        idempotencyKeyRef.current,
      );
      setSubmitState('success');
      setSubmitError('');
    } catch (error) {
      setSubmitError(
        getCustomerIntakeErrorMessage(
          error,
          'We could not submit your information. Please try again.',
        ),
      );
      setSubmitState('error');
    }
  };

  const resetForm = () => {
    if (!formConfig) return;
    setValues(createInitialCustomerIntakeValues(formConfig));
    setErrors({});
    setSubmitState('idle');
    setSubmitError('');
    idempotencyKeyRef.current = '';
  };

  const renderDescription = (field: CustomerIntakeField) => {
    const description = getCustomerIntakeFieldDescription(field.description);

    return description ? (
      <Typography variant="caption" color="text.secondary">
        {description}
      </Typography>
    ) : null;
  };

  const renderOptions = (options: CustomerIntakeFieldOption[] = []) =>
    options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ));

  const renderField = (field: CustomerIntakeField) => {
    if (!visibleField(field)) return null;

    const fieldError = errors[field.id];
    const fieldValue = values[field.id];

    if (field.type === 'service') {
      return (
        <Box key={field.id} className="customer-intake-field-group">
          <CustomerIntakeServiceField
            field={field}
            value={fieldValue}
            label={requiredLabel(field.label, field.required)}
            error={fieldError}
            helperText={getCustomerIntakeFieldDescription(field.description)}
            onChange={(nextValue) => handleValueChange(field.id, nextValue)}
          />
        </Box>
      );
    }

    if (
      field.type === 'text' ||
      field.type === 'custom' ||
      field.type === 'phone' ||
      field.type === 'email' ||
      field.type === 'date' ||
      field.type === 'number' ||
      field.type === 'textarea'
    ) {
      return (
        <Box key={field.id} className="customer-intake-field-group">
          <TextField
            fullWidth
            id={`customer-intake-${field.id}`}
            label={requiredLabel(field.label, field.required)}
            type={
              field.type === 'phone'
                ? 'tel'
                : field.type === 'email'
                  ? 'email'
                  : field.type === 'date'
                    ? 'date'
                    : field.type === 'number'
                      ? 'number'
                      : 'text'
            }
            value={typeof fieldValue === 'string' ? fieldValue : ''}
            placeholder={field.placeholder}
            onChange={(event) =>
              handleValueChange(field.id, event.target.value)
            }
            error={Boolean(fieldError)}
            helperText={
              fieldError ||
              getCustomerIntakeFieldDescription(field.description) ||
              undefined
            }
            multiline={field.type === 'textarea'}
            minRows={field.type === 'textarea' ? 3 : undefined}
            inputProps={
              field.id === 'additionalNotes' ? { maxLength: 500 } : undefined
            }
            InputLabelProps={
              field.type === 'date' ? { shrink: true } : undefined
            }
          />
        </Box>
      );
    }

    if (field.type === 'select') {
      return (
        <Box key={field.id} className="customer-intake-field-group">
          <FormControl fullWidth error={Boolean(fieldError)}>
            <InputLabel id={`customer-intake-${field.id}-label`}>
              {requiredLabel(field.label, field.required)}
            </InputLabel>
            <Select
              labelId={`customer-intake-${field.id}-label`}
              id={`customer-intake-${field.id}`}
              value={typeof fieldValue === 'string' ? fieldValue : ''}
              label={requiredLabel(field.label, field.required)}
              onChange={(event) =>
                handleValueChange(field.id, event.target.value)
              }
            >
              <MenuItem value="">
                <em>Select an option</em>
              </MenuItem>
              {renderOptions(field.options)}
            </Select>
            <FormHelperText>
              {fieldError ||
                getCustomerIntakeFieldDescription(field.description) ||
                undefined}
            </FormHelperText>
          </FormControl>
        </Box>
      );
    }

    if (field.type === 'radio') {
      return (
        <FormControl
          key={field.id}
          className="customer-intake-field-group"
          error={Boolean(fieldError)}
          component="fieldset"
        >
          <FormLabel component="legend">
            {requiredLabel(field.label, field.required)}
          </FormLabel>
          {renderDescription(field)}
          <RadioGroup
            value={typeof fieldValue === 'string' ? fieldValue : ''}
            onChange={(event) =>
              handleValueChange(field.id, event.target.value)
            }
            className={
              field.id === 'customerType'
                ? 'customer-intake-choice-grid'
                : undefined
            }
          >
            {(field.options || []).map((option) => {
              const isSelected = fieldValue === option.value;
              return (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                  className={`customer-intake-choice-card${isSelected ? ' is-selected' : ''}`}
                />
              );
            })}
          </RadioGroup>
          {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
        </FormControl>
      );
    }

    if (field.type === 'checkbox') {
      const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];

      return (
        <FormControl
          key={field.id}
          className="customer-intake-field-group"
          error={Boolean(fieldError)}
          component="fieldset"
        >
          <FormLabel component="legend">
            {requiredLabel(field.label, field.required)}
          </FormLabel>
          {renderDescription(field)}
          <Stack spacing={0.25}>
            {(field.options || []).map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={selectedValues.includes(option.value)}
                    onChange={() =>
                      handleCheckboxChange(field.id, option.value)
                    }
                  />
                }
                label={option.label}
              />
            ))}
          </Stack>
          {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
        </FormControl>
      );
    }

    return (
      <Box key={field.id} className="customer-intake-field-group">
        <FormControlLabel
          control={
            <Checkbox
              checked={fieldValue === true}
              onChange={(event) =>
                handleValueChange(field.id, event.target.checked)
              }
            />
          }
          label={requiredLabel(field.label, field.required)}
        />
        {fieldError && (
          <FormHelperText error sx={{ ml: 4 }}>
            {fieldError}
          </FormHelperText>
        )}
        {field.type === 'consent' && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
            Read our{' '}
            <Link
              component={RouterLink}
              to="/customer-survey/privacy"
              underline="hover"
            >
              Personal Data Protection Policy
            </Link>
            .
          </Typography>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="sm" className="customer-intake-page-container">
        <Card className="customer-intake-card customer-intake-state-card">
          <Stack spacing={2} alignItems="center">
            <CircularProgress color="primary" />
            <Typography color="text.secondary">
              Loading the customer information form...
            </Typography>
          </Stack>
        </Card>
      </Container>
    );
  }

  if (loadError || !formConfig) {
    return (
      <Container maxWidth="sm" className="customer-intake-page-container">
        <Card className="customer-intake-card customer-intake-state-card">
          <Stack spacing={2} alignItems="center">
            <InfoOutlined
              className="customer-intake-state-icon"
              aria-hidden="true"
            />
            <Typography variant="h4" color="secondary.main" fontWeight={700}>
              This form is currently unavailable
            </Typography>
            <Typography color="text.secondary" align="center">
              {loadError || 'Please contact the reception desk for assistance.'}
            </Typography>
            <Button variant="outlined" onClick={() => void loadForm()}>
              Try again
            </Button>
          </Stack>
        </Card>
      </Container>
    );
  }

  if (submitState === 'success') {
    return <CustomerIntakeSuccess onReset={resetForm} />;
  }

  const consentFields = formConfig.sections.flatMap((section) =>
    section.fields.filter(
      (field) => field.type === 'consent' && visibleField(field),
    ),
  );
  const consentSection =
    formConfig.sections.find((section) => section.id === 'confirmation') ||
    formConfig.sections.find((section) =>
      section.fields.some((field) => field.type === 'consent'),
    );

  return (
    <Container
      maxWidth="md"
      className="customer-intake-page-container customer-intake-form-container"
    >
      <Box className="customer-intake-hero">
        <Typography className="customer-intake-eyebrow">
          Aquatics HUB
        </Typography>
        <Typography
          component="h1"
          variant="h3"
          className="customer-intake-title"
          sx={{ mt: 1 }}
        >
          {formConfig.title}
        </Typography>
        <Typography variant="body1" className="customer-intake-subtitle">
          {formConfig.description} Estimated completion time:{' '}
          {formConfig.estimatedMinutes} minutes.
        </Typography>
      </Box>

      <Card className="customer-intake-card customer-intake-form-card">
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={0}>
            {formConfig.sections.map((section) => {
              const visibleFields = section.fields.filter(
                (field) => field.type !== 'consent' && visibleField(field),
              );

              if (visibleFields.length === 0) return null;

              return (
                <Box
                  key={section.id}
                  component="section"
                  className="customer-intake-form-section"
                >
                  <Stack spacing={0.75} sx={{ mb: 2.5 }}>
                    <Typography
                      component="h2"
                      variant="h6"
                      color="secondary.main"
                      fontWeight={700}
                    >
                      {section.title}
                    </Typography>
                    {section.description && (
                      <Typography variant="body2" color="text.secondary">
                        {section.description}
                      </Typography>
                    )}
                  </Stack>
                  <Stack spacing={2.5}>{visibleFields.map(renderField)}</Stack>
                </Box>
              );
            })}
          </Stack>

          {consentFields.length > 0 && (
            <Box
              component="section"
              className="customer-intake-form-section customer-intake-consent-section"
            >
              <Stack spacing={0.75} sx={{ mb: 2.5 }}>
                <Typography
                  component="h2"
                  variant="h6"
                  color="secondary.main"
                  fontWeight={700}
                >
                  {consentSection?.title || 'Confirmation'}
                </Typography>
                {consentSection?.description && (
                  <Typography variant="body2" color="text.secondary">
                    {consentSection.description}
                  </Typography>
                )}
              </Stack>
              <Stack spacing={2.5}>{consentFields.map(renderField)}</Stack>
            </Box>
          )}

          {submitState === 'error' && (
            <Box sx={{ mt: 2.5 }}>
              <CustomerIntakeError
                onRetry={() => setSubmitState('idle')}
                message={
                  submitError ||
                  'We could not submit your information. Please check your connection and try again.'
                }
              />
            </Box>
          )}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mt: 3 }}
          >
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={
                submitState === 'submitting' ? (
                  <CircularProgress color="inherit" size={18} />
                ) : (
                  <Send />
                )
              }
              disabled={submitState === 'submitting'}
            >
              {submitState === 'submitting'
                ? 'Submitting...'
                : 'Submit information'}
            </Button>
          </Stack>
        </Box>
      </Card>

      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
        <InfoOutlined fontSize="small" color="info" />
        <Typography variant="caption" color="text.secondary" align="center">
          Please provide only the information needed for your registration.
        </Typography>
      </Stack>
    </Container>
  );
};

export default function CustomerIntakeFormPage() {
  return (
    <CustomerIntakePublicShell className="customer-intake-form-page">
      <CustomerIntakeForm />
    </CustomerIntakePublicShell>
  );
}
